import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import { FormNavigationProvider, NavTextField, NavAutocomplete } from '../components/FormNavigation';
import { useAppDispatch, useAppSelector } from '../store';
import { setGlobalSettings, fetchGlobalSettings } from '../store/applicationSettingsSlice';
import { apiService } from '../services/api';
import { showNotification } from '../store/notificationSlice';
import type { HistoryTrackingUnit } from '../types/applicationSettings';

const IANA_TIMEZONES = [
  'UTC',
  'Asia/Dhaka',
  'Asia/Kolkata',
  'Asia/Karachi',
  'Asia/Dubai',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Seoul',
  'Asia/Riyadh',
  'Asia/Taipei',
  'Asia/Kuala_Lumpur',
  'Asia/Jakarta',
  'Asia/Kathmandu',
  'Asia/Colombo',
  'Asia/Kabul',
  'Asia/Tehran',
  'Asia/Baghdad',
  'Asia/Beirut',
  'Asia/Nicosia',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Africa/Cairo',
  'Africa/Nairobi',
  'Africa/Lagos',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Sao_Paulo',
  'Pacific/Auckland',
  'Pacific/Sydney',
];

export default function GlobalSettings() {
  const dispatch = useAppDispatch();
  const { globalSettings, isLoading } = useAppSelector((state) => state.applicationSettings);
  const navContainerRef = React.useRef<HTMLElement | null>(null);

  const [historyTrackingUnit, setHistoryTrackingUnit] = useState<HistoryTrackingUnit>('day');
  const [timekeepingTimezone, setTimekeepingTimezone] = useState<string>('UTC');
  const [deactivateClosestEntries, setDeactivateClosestEntries] = useState<boolean>(false);
  const [deactivateClosestEntriesThresholdSecond, setDeactivateClosestEntriesThresholdSecond] = useState<number>(0);
  const [timekeepingErrorDetectionModeStrict, setTimekeepingErrorDetectionModeStrict] = useState<boolean>(false);
  const [serverSideCaching, setServerSideCaching] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);

  // Sync from Redux if another page updates it
  useEffect(() => {
    if (globalSettings) {
      setHistoryTrackingUnit(globalSettings.historyTrackingUnit ?? 'day');
      setTimekeepingTimezone(globalSettings.timekeepingTimezone ?? 'UTC');
      setDeactivateClosestEntries(globalSettings.deactivateClosestEntries ?? false);
      setDeactivateClosestEntriesThresholdSecond(globalSettings.deactivateClosestEntriesThresholdSecond ?? 0);
      setTimekeepingErrorDetectionModeStrict(globalSettings.timekeepingErrorDetectionModeStrict ?? false);
      setServerSideCaching(globalSettings.serverSideCaching ?? false);
    }
  }, [globalSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiService.updateGlobalApplicationSettings({
        historyTrackingUnit,
        timekeepingTimezone,
        deactivateClosestEntries,
        deactivateClosestEntriesThresholdSecond,
        timekeepingErrorDetectionModeStrict,
        serverSideCaching,
      });
      dispatch(setGlobalSettings(res.data));
      dispatch(showNotification({ message: 'Global settings saved.', severity: 'success' }));
    } catch (err: any) {
      dispatch(showNotification({ message: err?.message ?? 'Failed to save global settings', severity: 'error' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <FormNavigationProvider containerRef={navContainerRef}>
        <Box ref={navContainerRef} sx={{ display: 'flex', flexDirection: 'column', m: -3, minHeight: 'calc(100vh - 64px)' }}>
          <Box sx={{ flexGrow: 1, p: 3, maxWidth: 700 }}>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Typography variant="h5" fontWeight={600}>
              Global Settings
            </Typography>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => dispatch(fetchGlobalSettings())} disabled={isLoading} sx={{ mt: '2px' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              Employee Entry
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 240, flexShrink: 0 }}>
                History Tracking Unit
              </Typography>
              {isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <ToggleButtonGroup
                  value={historyTrackingUnit}
                  exclusive
                  onChange={(_, val) => { if (val) setHistoryTrackingUnit(val); }}
                  size="small"
                >
                  <ToggleButton value="day">By Day</ToggleButton>
                  <ToggleButton value="month">By Month</ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              Timekeeping
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 240, flexShrink: 0, pt: 1 }}>
                  Timezone
                </Typography>
                {isLoading ? (
                  <CircularProgress size={20} sx={{ mt: 1 }} />
                ) : (
                  <NavAutocomplete
                    value={timekeepingTimezone}
                    onChange={(_, val) => { if (val) setTimekeepingTimezone(val); }}
                    options={IANA_TIMEZONES}
                    freeSolo
                    size="small"
                    sx={{ width: 300 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="e.g. Asia/Dhaka"
                        helperText="IANA timezone used for manual time entries"
                      />
                    )}
                  />
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 240, flexShrink: 0 }}>
                  Deactivate Closest Entries
                </Typography>
                {isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={deactivateClosestEntries}
                        onChange={(e) => setDeactivateClosestEntries(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={deactivateClosestEntries ? "Enabled" : "Disabled"}
                    sx={{ m: 0 }}
                  />
                )}
              </Box>

              {deactivateClosestEntries && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 240, flexShrink: 0, pt: 1 }}>
                    Threshold (seconds)
                  </Typography>
                  {isLoading ? (
                    <CircularProgress size={20} sx={{ mt: 1 }} />
                  ) : (
                    <NavTextField
                      type="number"
                      value={deactivateClosestEntriesThresholdSecond}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        setDeactivateClosestEntriesThresholdSecond(isNaN(val) ? 0 : val);
                      }}
                      size="small"
                      sx={{ width: 300 }}
                      helperText="Time window in seconds during which consecutive entries are deactivated"
                    />
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 240, flexShrink: 0 }}>
                  Timekeeping Error Detection Strict
                </Typography>
                {isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={timekeepingErrorDetectionModeStrict}
                        onChange={(e) => setTimekeepingErrorDetectionModeStrict(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={timekeepingErrorDetectionModeStrict ? "Strict" : "Loose"}
                    sx={{ m: 0 }}
                  />
                )}
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box sx={{ display: 'flex', alignItems: 'flex-start', minHeight: 40 }}>
                <Box sx={{ width: 240, flexShrink: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    Server-Side Caching
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Enables high-performance memory caching. Turning on resets consecutive failure counters.
                  </Typography>
                </Box>
                {isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={serverSideCaching}
                        onChange={(e) => setServerSideCaching(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={serverSideCaching ? "Enabled" : "Disabled"}
                    sx={{ m: 0 }}
                  />
                )}
              </Box>
            </Box>
          </Paper>

        </Box>

        {/* Sticky action bar */}
        <Paper elevation={6} sx={{ position: 'sticky', bottom: 0, px: 3, py: 1.5, display: 'flex', gap: 1, borderRadius: 0, mt: 'auto' }}>
          <Button variant="contained" onClick={handleSave} disabled={saving || isLoading}>
            {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Save
          </Button>
          <Button variant="outlined" onClick={() => dispatch(fetchGlobalSettings())} disabled={isLoading || saving}>
            Reset
          </Button>
        </Paper>
      </Box>
      </FormNavigationProvider>
    </DashboardLayout>
  );
}
